import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PhoneIncoming, Phone, PhoneMissed, PhoneOff, X, User } from 'lucide-react';
import { useLiveCall } from '../../../core/telephony/useLiveCall';
import { usePhonePresence, serverNow } from '../../../core/telephony/usePhonePresence';
import { liveView, mmss, incomingSummary, shouldPopIncoming, savedLabel } from '../../../core/telephony/liveCall';

// LiveCallLayer — "one call, one screen" on the laptop (Caller 4.30+).
//
// Driven by tc/live/{uid}, which the proxy keeps from the phone's own reports:
//   • an INCOMING call pops a card top-right the moment the phone rings —
//     "📞 Lakshmi calling — last order HP8 ₹896 · follow-up due today" — plus a
//     desktop notification when the agent has allowed them (asked once, politely);
//   • a slim bar follows every call, in or out: Ringing → Connected mm:ss → Ended,
//     and says when the outcome was saved on the phone.
// Only for an agent with a linked phone; everyone else never even listens.

const PROXY = (import.meta.env?.VITE_PROXY_URL || (import.meta.env?.DEV ? 'http://localhost:3001' : '')).replace(/\/$/, '');
const getJson = async (path) => {
    const { authHeaders } = await import('../../../core/api/authHeaders');
    const r = await fetch(`${PROXY}${path}`, { headers: { ...(await authHeaders()) } });
    return r.json().catch(() => ({}));
};

const NOTIF_ASKED = 'daksha.liveCall.notifAsked';
const lsGet = (k) => { try { return window.localStorage.getItem(k); } catch { return null; } };
const lsSet = (k, v) => { try { window.localStorage.setItem(k, v); } catch { /* private window */ } };
const notifSupported = () => typeof window !== 'undefined' && 'Notification' in window;

const IncomingCallPopup = ({ live, view, onClose }) => {
    const navigate = useNavigate();
    const [summary, setSummary] = useState('');
    const [askNotif, setAskNotif] = useState(() => notifSupported() && Notification.permission === 'default' && !lsGet(NOTIF_ASKED));
    const notified = useRef('');
    const mobile = live?.mobile || '';

    useEffect(() => {
        let gone = false;
        setSummary('');
        if (!/^\d{10}$/.test(mobile)) return undefined;
        getJson(`/api/tc/customer360?mobile=${mobile}`)
            .then(j => { if (!gone && j && j.ok !== false) setSummary(incomingSummary(j)); })
            .catch(() => {});
        return () => { gone = true; };
    }, [mobile]);

    // The desktop alert — once per call, and only when the name is known.
    useEffect(() => {
        if (!notifSupported() || Notification.permission !== 'granted') return;
        if (!live?.callKey || !mobile || notified.current === live.callKey) return;
        notified.current = live.callKey;
        try {
            const n = new Notification(`📞 ${view.name} calling`, { body: summary || 'Incoming call on your phone', tag: live.callKey, silent: false });
            n.onclick = () => { window.focus(); navigate(`/customers/${mobile}`); n.close(); };
        } catch { /* some browsers only allow notifications from a service worker */ }
    }, [live?.callKey, mobile, summary]); // eslint-disable-line react-hooks/exhaustive-deps

    const allow = async () => {
        lsSet(NOTIF_ASKED, '1');
        setAskNotif(false);
        try { await Notification.requestPermission(); } catch { /* ignore */ }
    };

    return (
        <div role="alertdialog" aria-label="Incoming call"
            className="fixed top-16 right-4 z-[120] w-[340px] max-w-[94vw] rounded-2xl border border-green-300 bg-white shadow-2xl overflow-hidden">
            <div className="flex items-center gap-2 bg-green-50 px-4 py-2.5 text-sm font-semibold text-green-800">
                <PhoneIncoming size={16} className="animate-pulse" />
                <span className="truncate">📞 {view.name} calling</span>
                <span className="ml-auto font-mono text-xs">{mmss(view.timerMs)}</span>
                <button type="button" onClick={onClose} className="p-1 rounded hover:bg-black/5" aria-label="Hide"><X size={14} /></button>
            </div>
            <div className="p-4 space-y-2 text-sm">
                {mobile ? (
                    <>
                        <div className="text-slate-700">{summary || <span className="text-slate-400">Looking up the customer…</span>}</div>
                        <div className="text-[11px] text-slate-400 font-mono">{mobile}</div>
                        <button type="button" onClick={() => navigate(`/customers/${mobile}`)}
                            className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700">
                            <User size={13} /> Open profile
                        </button>
                    </>
                ) : (
                    <div className="text-xs text-slate-500">Answer on your phone. The customer card appears here if the number is a customer.</div>
                )}
                {askNotif && (
                    <div className="mt-1 rounded-lg bg-slate-50 p-2 text-[11px] text-slate-600">
                        Want a desktop alert when your phone rings, even in another tab?
                        <div className="mt-1.5 flex gap-2">
                            <button type="button" onClick={allow} className="rounded-md bg-green-600 px-2 py-1 font-semibold text-white">Allow alerts</button>
                            <button type="button" onClick={() => { lsSet(NOTIF_ASKED, '1'); setAskNotif(false); }} className="rounded-md px-2 py-1 text-slate-500 hover:bg-slate-100">No thanks</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const LiveCallBar = ({ view, onOpen, onHide }) => {
    const tone = view.phase === 'connected' ? 'bg-green-600 text-white'
        : view.phase === 'ringing' ? 'bg-amber-500 text-white'
            : view.phase === 'missed' ? 'bg-red-600 text-white'
                : 'bg-slate-700 text-white';
    const Icon = view.phase === 'missed' ? PhoneMissed : view.phase === 'ended' ? PhoneOff : view.out ? Phone : PhoneIncoming;
    const right = view.phase === 'connected' ? mmss(view.timerMs)
        : view.phase === 'ringing' ? mmss(view.timerMs) : '';
    const outcomeNote = (view.phase === 'ended' || view.phase === 'missed')
        ? (view.saved ? savedLabel(view.savedBy) : (view.phase === 'ended' ? 'log the outcome' : 'call them back'))
        : '';
    return (
        <div className="fixed top-2 left-1/2 -translate-x-1/2 z-[110] max-w-[94vw]">
            <div className={`flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold shadow-lg ${tone}`}>
                {view.phase === 'connected' && <span className="h-2 w-2 rounded-full bg-white animate-pulse" />}
                <Icon size={14} />
                <span className="truncate max-w-[42vw]">{view.label}</span>
                {right && <span className="font-mono tabular-nums">{right}</span>}
                {outcomeNote && <span className="opacity-90">· {outcomeNote}</span>}
                {view.mobile && (
                    <button type="button" onClick={onOpen} className="ml-1 rounded-full bg-white/20 px-2 py-0.5 hover:bg-white/30">Open profile</button>
                )}
                <button type="button" onClick={onHide} className="rounded-full p-0.5 hover:bg-white/20" aria-label="Hide the call bar"><X size={12} /></button>
            </div>
        </div>
    );
};

const LiveCallLayer = () => {
    const navigate = useNavigate();
    const { view: phone } = usePhonePresence();
    // Only an agent who has linked a phone listens at all.
    const linked = !!phone && phone.status !== 'none';
    const live = useLiveCall(linked);
    const [, setTick] = useState(0);
    const [hiddenBar, setHiddenBar] = useState('');      // callKey:phase the agent hid
    const [popupKey, setPopupKey] = useState('');
    const shown = useRef(new Set());

    useEffect(() => {
        if (!live) return undefined;
        const t = setInterval(() => setTick(x => x + 1), 1000);
        return () => clearInterval(t);
    }, [live]);

    const now = serverNow();
    const view = liveView(live, now);

    // Pop the incoming card once per ringing call.
    useEffect(() => {
        if (shouldPopIncoming(live, shown.current, serverNow())) {
            shown.current.add(live.callKey);
            setPopupKey(live.callKey);
        }
    }, [live]);

    if (!linked || view.phase === 'none') return null;
    const popup = popupKey && live?.callKey === popupKey && view.phase === 'ringing' && !view.out;
    const barKey = `${view.callKey}:${view.phase}`;
    return (
        <>
            {hiddenBar !== barKey && (
                <LiveCallBar view={view} onOpen={() => navigate(`/customers/${view.mobile}`)} onHide={() => setHiddenBar(barKey)} />
            )}
            {popup && <IncomingCallPopup live={live} view={view} onClose={() => setPopupKey('')} />}
        </>
    );
};

export default LiveCallLayer;
